/* global window */
/* eslint-disable complexity, max-statements, consistent-return */
'use strict';

import {bootstrapFragment, bootstrapFragments} from './bootstrap';
import {isString, parallel, escapeSelector} from './utils';
import parseJSON from './parseJSON';
import {getJSON} from './xhr';
import opt from './options';
import log from './logger';
import docReady from './documentReady';
import scriptLoader from './loaders/scriptLoader';
import linkLoader from './loaders/linkLoader';
import htmlLoader from './loaders/htmlLoader';
import i18nLoader from './loaders/i18nLoader';
import event from './event';
import merge from './merge';

let integrationPointsToLoad = [];
let isDocReady = false;

const keysSorted = Object.keys(opt.byType || {}).sort((type1, type2) => opt.byType[type1].order - opt.byType[type2].order);

docReady(() => {
    isDocReady = true;
    // bootstrap after document ready when all integration points are downloaded.
    bootstrap();
});

window.APP_CONFIG = window.APP_CONFIG || {};
window.APP_CONFIG.FRAGMENTS_CONFIG = window.APP_CONFIG.FRAGMENTS_CONFIG || {};

/**
 * Starts download phase for fragments not loaded and bootstrap phase for fragments already loaded.
 * if fragment type is singleBootstrap=true, we must wait for all integration points and fragments to be loaded, and document ready before bootstrapping
 * if not, bootstrap every fragments individually.
 */
function bootstrap() {
    log.debug('bootstrap()');

    keysSorted.forEach((type) => {
        const opts = opt.byType[type];
        const fragments = opts.fragments || [];
        const fragmentsLoaded = [];

        fragments.forEach((fragment) => {
            if (fragment.loaded) {
                fragmentsLoaded.push(fragment);
            } else if (!fragment.loading) {
                loadFragmentFiles(fragment);
            }
        });

        if (opts.singleBootstrap) {
            // after document ready, when all integration points and fragments have been loaded, bootstrap fragments together.
            // we must wait for document ready because some fragments and integration points could be cached and loaded before document ready,
            // and bootstrap before all integration points are discovered in the page.
            if (isDocReady && integrationPointsToLoad.length === 0 && fragmentsLoaded.length === fragments.length && !opts.bootstrapped) {
                fragments.forEach((fragment) => event.trigger('beforeBootstrap', {fragment}));

                bootstrapFragments(fragments, type);

                opts.bootstrapped = true;

                fragments.forEach((fragment) => {
                    fragment.bootstrapped = true;
                    event.trigger('afterBootstrap', {fragment});
                });
            }
        } else {
            fragmentsLoaded.forEach((fragment) => {
                if (!fragment.bootstrapped) {
                    event.trigger('beforeBootstrap', {fragment});

                    bootstrapFragment(fragment);
                    fragment.bootstrapped = true;

                    event.trigger('afterBootstrap', {fragment});
                }
            });
        }
    });

    const isBoostrapped = !Object.keys(opt.byType || {}).some((type) => {
        return opt.byType[type].fragments && opt.byType[type].fragments.some((fragment) => !fragment.bootstrapped);
    });

    if (isDocReady && integrationPointsToLoad.length === 0 && isBoostrapped) {
        event.trigger('end');
    }

    return this;
}

function loadFragmentFiles(fragment) {
    log.debug(`loadFragmentFiles(): ${fragment.name} ${fragment.version || ''}`);
    fragment.loading = true;

    if (fragment.files && fragment.files.length) {
        const filesByType = {i18n: [], js: [], link: [], html: []};

        fragment.files.forEach((file) => {
            if (file) {
                if (file.file) {
                    file.url = `${opt.fewBaseUrl}/${isString(file.file) ? file.file : file.file[opt.lang]}`;
                }
                filesByType[file.type === 'css' ? 'link' : file.type].push(file);
            }
        });

        // insert html in DOM before bootstrapping and before loading links/scripts
        // e.g. js file in marginals runs querySelector without waiting for doc ready.
        htmlLoader(filesByType.html, (err1) => {
            if (err1) {
                event.trigger('error.fragment', {error: err1, fragment});
                fragment.loaded = true;
                fragment.error = !!err1;
                bootstrap();
                return;
            }

            parallel([
                (cb) => i18nLoader(filesByType.i18n, cb),
                (cb) => scriptLoader(filesByType.js, cb),
                (cb) => linkLoader(filesByType.link, cb)
            ], (err2) => {
                // apply configs in order (i18n, configuration, overrides)
                filesByType.i18n.forEach((file) => {
                    mergeConfig({[fragment.name]: {i18n: {[opt.lang]: file.json}}});
                });
                mergeConfig({[fragment.name]: fragment.config});
                mergeConfig({[fragment.name]: opt.fragmentsConfigOverride[fragment.name]});
                mergeConfig({[fragment.name]: {i18n: {[opt.lang]: parseJSON(`script[v-few-frg-loader-i18n=${escapeSelector(fragment.name)}]`)}}});

                fragment.loaded = true;
                fragment.error = !!err2;

                if (err2) {
                    event.trigger('error.fragment', {error: err2, fragment});
                } else {
                    event.trigger('load.fragment', {fragment});
                }

                bootstrap();
            });
        });
    } else {
        // when there is no fragment or files to download, start bootstrap.
        // needed because it could be the last integration point loaded in single bootstrap mode.
        fragment.loaded = true;
        bootstrap();
    }
}

/**
 * Load config and fragments for each integration points.
 *
 * @param {string|string[]} integrationPoints integration point or array of integration points
 * @param {boolean} skipFiles do not load fragment files (i.e. if already included in html)
 */
function loadIntegrationPoints(integrationPoints, skipFiles = false) {
    const arr = Array.isArray(integrationPoints) ? integrationPoints : [integrationPoints];

    arr.forEach((integrationPoint) => {
        loadIntegrationPoint(integrationPoint, skipFiles);
    });
    return this;
}

function loadIntegrationPoint(integrationPoint, skipFiles) {
    integrationPointsToLoad.push(integrationPoint);

    if (opt.waitForDocReady) {
        docReady(() => doLoadIntegrationPoint(integrationPoint, skipFiles));
    } else {
        doLoadIntegrationPoint(integrationPoint, skipFiles);
    }
}

function doLoadIntegrationPoint(integrationPoint, skipFiles) {
    getJSON(`${opt.fewBaseUrl}/a/few/frg-config/${integrationPoint}.json`, (err, data) => {
        const indexIntegrationPoint = integrationPointsToLoad.indexOf(integrationPoint);

        log.debug(`doLoadIntegrationPoint(): ${integrationPoint}, index:${indexIntegrationPoint}`);
        integrationPointsToLoad[indexIntegrationPoint] = {data, skipFiles};

        if (err) {
            event.trigger('error.integrationPoint', {error: err, integrationPoint: {name: integrationPoint}});
            log.debug(`doLoadIntegrationPoint() error: ${integrationPoint} ${err}`);

            // remove integrationPoint from array
            integrationPointsToLoad.splice(indexIntegrationPoint, 1);
        }

        // load all integration points in order (load integration point objects only, string aren't loaded yet)
        // loop through all objects until a string (integration point not loaded) is found.
        while (!isString(integrationPointsToLoad[0] || '')) {
            const obj = integrationPointsToLoad.shift();

            parseIntegrationPoint(obj.data, obj.skipFiles);
        }
    });
}

function parseIntegrationPoint(data, skipFiles) {
    event.trigger('load.integrationPoint', {integrationPoint: data});

    data.appConfig = data.appConfig || {};

    // apply configs in order (configuration, overrides)
    mergeConfig(data.appConfig);
    mergeConfig(opt.fragmentsConfigOverride);

    if (data.fragments) {
        data.fragments.forEach((fragment) => {
            const typeOptions = opt.byType[fragment.type];

            if (skipFiles || opt.skipFiles) {
                delete fragment.files;
            }

            fragment.config = data.appConfig[fragment.name];
            typeOptions.fragments = typeOptions.fragments || [];

            // do not insert duplicate fragments
            if (!typeOptions.fragments.some((frg) => frg.name === fragment.name)) {
                typeOptions.fragments.push(fragment);
            }
        });
    }

    bootstrap();
}

function mergeConfig(config) {
    if (config) {
        for (let prop in config) {
            if (config.hasOwnProperty(prop)) {
                window.APP_CONFIG.FRAGMENTS_CONFIG[prop] = window.APP_CONFIG.FRAGMENTS_CONFIG[prop] || {};
                merge(window.APP_CONFIG.FRAGMENTS_CONFIG[prop] || {}, config[prop]);
            }
        }
    }
}

/**
 * Manually add a fragment to boostrap. Useful in cases like OC11 TMO where the main angular app isn't loaded from few servers.
 * @param fragment fragment to add to boostrapping ({type: '', name: '', module: ''})
 */
function initFragment(fragment) {
    if (!fragment || !fragment.type) {
        return;
    }

    opt.byType[fragment.type].fragments = opt.byType[fragment.type].fragments || [];
    opt.byType[fragment.type].fragments.push(fragment);
    bootstrap();
    return this;
}

function init(config) {
    merge(opt, config);
    return this;
}

window.vlFragmentLoader = {
    init: init,
    on: event.on,
    off: event.off,
    initFragment: initFragment,
    bootstrap: bootstrap,
    loadIntegrationPoints: loadIntegrationPoints,
    /* deprecated */
    loadFragments: loadIntegrationPoints
};
